import { useEffect, useState } from 'preact/hooks'
import { useLocation } from 'preact-iso'
import { ArrowLeft, AlertCircle } from 'lucide-preact'
import { routes } from '@/utils/routes'
import { usePageHeader } from '@/hooks/usePageHeader'
import { useEmailBlastDetail } from '@/hooks/useEmailBlastDetail'
import { useBlastLive } from '@/hooks/useBlastLive'
import { USER_ROLES, useUserProfile } from '@/hooks/useUserProfile'
import { ConfirmationModal } from '@/components/modals/ConfirmationModal'
import { BlastStatusSpine } from './spine/BlastStatusSpine'
import { DeliverySection } from './DeliverySection'
import { RolloutLogDisclosure } from './RolloutLogDisclosure'
import { SplitTestSkippedPanel } from './SplitTestSkippedPanel'
import { BlastEngagementSection } from './BlastEngagementSection'
import { BlastRecipientsSection } from './BlastRecipientsSection'

interface Props {
  clientCompanyId: string
  workspaceId: string
  campaignId: string
  webId: string
  blastId: string
}

/**
 * Deep-linkable blast detail (ORB-384), recomposed around a status spine (ORB-706).
 *
 * The page used to present seven sections at identical weight, so the question an operator opens it to
 * answer — is this send healthy, and do I need to intervene — was answered fourth, by two rate meters
 * drawing their numbers from a stale aggregate. Now the spine answers it first and everything below is
 * the evidence, ordered by how often anyone needs it: Delivery (what happened to the mail), Engagement
 * (did it work), Recipients (did one particular person get it), Rollout log (why the system did that).
 */
export function BlastDetailPage({ clientCompanyId, workspaceId, campaignId, webId, blastId }: Props) {
  const location = useLocation()
  const { setHeader } = usePageHeader()
  const { blast, plan, loading, error, actioning, resumeRefusal, load, refetchQuietly, resume, cancel } =
    useEmailBlastDetail({ workspaceId, blastId })
  const [confirm, setConfirm] = useState<'resume' | 'cancel' | null>(null)
  const { userCompanyRoleSets, hasCompanyRole } = useUserProfile()

  // Refetch when the engine reports this blast moved. Quietly: a background refresh must not drive the
  // spine's Refresh spinner, or it strobes on its own every five minutes.
  useBlastLive(workspaceId, blastId, refetchQuietly)

  const scoped = routes.scoped(clientCompanyId, workspaceId)
  const broadcastsHref = `${scoped.web(campaignId, webId)}/email-sends`

  // Sending Domains is a company-admin settings screen (mirrors SettingsNavigation), so only an admin is
  // given a link to it; everyone else reads the domain's name as plain text.
  const isCompanyAdmin =
    (userCompanyRoleSets?.find(roleSet => roleSet.ClientCompanyId === clientCompanyId)?.IsAdmin ?? false) ||
    hasCompanyRole(USER_ROLES.ADMIN, clientCompanyId)
  const domainHref = isCompanyAdmin ? `/company/${clientCompanyId}/settings/sending-domains` : null

  useEffect(() => {
    setHeader({
      items: [
        { text: 'Campaigns', href: scoped.campaignsIndex() },
        { text: 'Campaign', href: scoped.campaign(campaignId) },
        { text: 'Web', href: scoped.web(campaignId, webId) },
        { text: 'Broadcasts', href: broadcastsHref },
        { text: blast?.Subject || 'Blast' },
      ],
    })
  }, [clientCompanyId, workspaceId, campaignId, webId, blast?.Subject])

  const goBack = () => {
    try {
      location.route(broadcastsHref)
    } catch {
      if (typeof window !== 'undefined') window.history.back()
    }
  }

  if (loading && !blast) {
    return (
      <div className="flex items-center gap-3 p-6 text-slate-600 dark:text-slate-300">
        <div className="h-5 w-5 animate-spin rounded-full border-2 border-primary-600 border-t-transparent" />
        Loading blast…
      </div>
    )
  }

  if (error && !blast) {
    return (
      <div className="space-y-4 p-6">
        <BackLink onClick={goBack} />
        <div className="flex items-start gap-2 rounded-lg border border-red-200 bg-red-50 p-4 text-sm text-red-700 dark:border-red-800 dark:bg-red-900/30 dark:text-red-300">
          <AlertCircle className="mt-0.5 h-4 w-4" />
          <span>{error}</span>
        </div>
      </div>
    )
  }

  if (!blast) return null

  return (
    <div className="max-w-5xl space-y-8 p-6">
      <BackLink onClick={goBack} />

      {/* A refresh that failed while figures are already on screen. Previously this branch was gated on
          !blast, so it only ever fired on the FIRST load — every later failure left stale numbers up
          with no indication. With a realtime refetch every few minutes that goes from rare to routine. */}
      {error && (
        <p className="flex items-start gap-2 rounded-lg border border-amber-200 bg-amber-50 px-3 py-2 text-sm text-amber-800 dark:border-amber-800 dark:bg-amber-900/20 dark:text-amber-300">
          <AlertCircle className="mt-0.5 h-4 w-4 shrink-0" />
          <span>Couldn’t refresh — the figures below may be out of date.</span>
        </p>
      )}

      <BlastStatusSpine
        blast={blast}
        plan={plan}
        domainHref={domainHref}
        subject={blast.Subject ?? null}
        loading={loading}
        actioning={actioning}
        resumeRefusal={resumeRefusal}
        onRefresh={() => void load()}
        onResume={() => setConfirm('resume')}
        onCancel={() => setConfirm('cancel')}
      />

      {/* A configured A/B test that couldn't run is a configuration failure, so it stays above the fold
          rather than moving into the Rollout log with the rest of the split-test material (ORB-472). */}
      {!blast.IsSplitTest && blast.SplitTestSkip && <SplitTestSkippedPanel skip={blast.SplitTestSkip} />}

      {blast.WinningVariantKey && (
        <p className="text-sm text-slate-600 dark:text-slate-300">
          Subject <span className="font-semibold">{blast.WinningVariantKey}</span> won on{' '}
          {(blast.WinMetric ?? 'opens').toLowerCase()}; it sends to the remaining recipients.
        </p>
      )}

      <DeliverySection blast={blast} />
      <BlastEngagementSection engagement={blast.Engagement} />
      <BlastRecipientsSection
        workspaceId={workspaceId}
        clientCompanyId={clientCompanyId}
        campaignId={campaignId}
        webId={webId}
        blastId={blastId}
      />
      <RolloutLogDisclosure blast={blast} />

      <ConfirmationModal
        isOpen={confirm === 'resume'}
        title="Resume blast"
        message="Resume sending to the remaining audience? Sending will continue from where it paused, subject to the same bounce and complaint limits."
        type="info"
        confirmText="Resume"
        loading={actioning}
        onConfirm={() => {
          setConfirm(null)
          void resume()
        }}
        onCancel={() => setConfirm(null)}
      />
      <ConfirmationModal
        isOpen={confirm === 'cancel'}
        title="Cancel blast"
        message="Cancel this blast? Any unsent emails will not be sent. This cannot be undone."
        type="danger"
        confirmText="Cancel blast"
        cancelText="Keep sending"
        loading={actioning}
        onConfirm={() => {
          setConfirm(null)
          void cancel()
        }}
        onCancel={() => setConfirm(null)}
      />
    </div>
  )
}

function BackLink({ onClick }: { onClick: () => void }) {
  return (
    <button
      onClick={onClick}
      className="inline-flex items-center gap-1.5 text-sm text-slate-600 hover:text-slate-900 dark:text-slate-300 dark:hover:text-white"
    >
      <ArrowLeft className="h-4 w-4" /> Broadcasts
    </button>
  )
}
